import React, { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
    validateFirstname,
    ContactTypes,
    validateLastname,
    validateContactEmail,
    validateMobileNumber,
} from '../../../Validation';
import some from 'lodash/some';
import { SectionProps } from '../../types';
import Panel from "@sharedComponents/Panel";
import InputText from "@sharedComponents/InputText";

const CombinedForm = (props: SectionProps) => {
    const {
        stepValues,
        setStepValues,
        onChangeValues,
        formErrors,
        setFormErrors,
    } = props
    const { t } = useTranslation()

    const onChangeFirstname = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        const obj = {
            ...stepValues.contactInfo,
            combined: {
                ...stepValues.contactInfo?.combined,
                firstname: value,
            },
        }

        setStepValues({ ...stepValues, contactInfo: obj })
        onChangeValues && onChangeValues({ contactInfo: obj })

        const errors = validateFirstname(
            value,
            formErrors,
            ContactTypes.Combined
        )
        setFormErrors(errors)
    }

    const onChangeLastname = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        const obj = {
            ...stepValues.contactInfo,
            combined: {
                ...stepValues.contactInfo?.combined,
                lastname: value,
            },
        }

        setStepValues({ ...stepValues, contactInfo: obj })
        onChangeValues && onChangeValues({ contactInfo: obj })

        const errors = validateLastname(
            value,
            formErrors,
            ContactTypes.Combined
        )
        setFormErrors(errors)
    }

    const onChangeEmail = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        const obj = {
            ...stepValues.contactInfo,
            combined: {
                ...stepValues.contactInfo?.combined,
                email: value,
            },
        }

        setStepValues({ ...stepValues, contactInfo: obj })
        props.onChangeValues && props.onChangeValues({ contactInfo: obj })

        const errors = validateContactEmail(
            value,
            formErrors,
            ContactTypes.Combined
        )
        setFormErrors(errors)
    }

    const onChangeMobile = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        const obj = {
            ...stepValues.contactInfo,
            combined: {
                ...stepValues.contactInfo?.combined,
                mobileNumber: value,
            },
        }

        setStepValues({ ...stepValues, contactInfo: obj })
        props.onChangeValues && props.onChangeValues({ contactInfo: obj })

        const errors = validateMobileNumber(
            value,
            formErrors,
            ContactTypes.Combined
        )
        setFormErrors(errors)
    }

    const onChangePhone = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        const obj = {
            ...stepValues.contactInfo,
            combined: {
                ...stepValues.contactInfo?.combined,
                phoneNumber: value,
            },
        }
        setStepValues({ ...stepValues, contactInfo: obj })
        props.onChangeValues && props.onChangeValues({ contactInfo: obj })
    }

    const uniqueErrors = (key: string) => {
        const errors = formErrors.find(err => err.key === key);
        const uniqueErr = errors ? [...new Set(errors.messages)] : [];

        return uniqueErr.length > 0 ? uniqueErr.join(', ') : '';
    }

    return (
        <div className='producer-form producer-panel-wrapper'>
            <Panel className="panel--crb">
                <h3 className="panel--crb__combined-heading">
                    {t('companyRegistration.combined')}
                </h3>
                <div className='producer-form__fields'>
                    <div className='producer-form__field'>
                        <InputText
                            id="crb-comb-firstname"
                            label={t('global.firstname')}
                            required={true}
                            hasError={
                                !!some(formErrors, [
                                    'key',
                                    `${ContactTypes.Combined}Firstname`,
                                ])
                            }
                            value={
                                stepValues.contactInfo?.combined?.firstname ||
                                ''
                            }
                            maxLength={50}
                            onChange={onChangeFirstname}
                            autoComplete="given-name"
                            errorText={uniqueErrors(`${ContactTypes.Combined}Firstname`)}
                        />
                    </div>
                    <div className='producer-form__field'>
                        <InputText
                            id="crb-comb-lastname"
                            label={t('global.lastname')}
                            required={true}
                            hasError={
                                !!some(formErrors, [
                                    'key',
                                    `${ContactTypes.Combined}Lastname`,
                                ])
                            }
                            value={
                                stepValues.contactInfo?.combined?.lastname || ''
                            }
                            maxLength={50}
                            onChange={onChangeLastname}
                            autoComplete="family-name"
                            errorText={uniqueErrors(`${ContactTypes.Combined}Lastname`)}
                        />
                    </div>
                    <div className='producer-form__field'>
                        <InputText
                            id="crb-comb-email"
                            label={t('global.email')}
                            hasError={
                                !!some(formErrors, [
                                    'key',
                                    `${ContactTypes.Combined}Email`,
                                ])
                            }
                            required={true}
                            value={
                                stepValues.contactInfo?.combined?.email || ''
                            }
                            onChange={onChangeEmail}
                            type="email"
                            autoComplete="email"
                            maxLength={250}
                            errorText={uniqueErrors(`${ContactTypes.Combined}Email`)}
                        />
                    </div>
                    <div className='producer-form__field'>
                        <InputText
                            id="crb-comb-mobile"
                            label={t('global.mobileNumber')}
                            required={true}
                            hasError={
                                !!some(formErrors, [
                                    'key',
                                    `${ContactTypes.Combined}MobileNumber`,
                                ])
                            }
                            value={
                                stepValues.contactInfo?.combined
                                    ?.mobileNumber || ''
                            }
                            maxLength={30}
                            onChange={onChangeMobile}
                            autoComplete="tel"
                            errorText={uniqueErrors(`${ContactTypes.Combined}MobileNumber`)}
                        />
                    </div>
                    <div className='producer-form__field'>
                        <InputText
                            id="crb-comb-phone"
                            label={t('global.phoneNumber')}
                            value={
                                stepValues.contactInfo?.combined?.phoneNumber ||
                                ''
                            }
                            maxLength={30}
                            onChange={onChangePhone}
                            autoComplete="tel"
                        />
                    </div>
                </div>
            </Panel>
        </div>
    )
}

export default CombinedForm;